/**
 * Typed REST + SSE client for the @novelist/api Worker.
 *
 * The base URL is read from `import.meta.env.VITE_API_BASE` at build time.
 * In local dev (`pnpm --filter @novelist/app-web dev`), Vite proxies
 * `/api/*` to the API Worker on port 8787 — see vite.config.ts.
 */

import type { RunConfig } from "@novelist/schema/runConfig";

export type RunStatus =
  | "queued"
  | "running"
  | "paused"
  | "checkpoint_pending"
  | "completed"
  | "failed"
  | "cancelled"
  | "errored";

export interface RunRow {
  id: string;
  premise: string;
  genreKey: string;
  provider: string;
  model: string;
  targetChapters: number;
  wordsPerChapter: number;
  checkpointEvery: number;
  maxRevisePasses: number;
  register: "cholit" | "sadhu";
  status: RunStatus;
  currentChapter: number | null;
  workflowInstanceId: string | null;
  config: RunConfig;
  createdAt: string;
  updatedAt: string;
  errorMessage: string | null;
  coverUrl?: string | null;
}

export interface ChapterRow {
  id: string;
  runId: string;
  chapterNo: number;
  title: string | null;
  wordCount: number | null;
  revisePasses: number | null;
  continuityStatus: string | null;
  thumbnailUrl?: string | null;
  draftedAt: string;
}

export interface BibleSummary {
  title: string;
  logline: string;
}

export interface ProgressEvent {
  kind?: string;
  type?: string;
  runId: string;
  chapterNo?: number;
  status?: string;
  message?: string;
  at?: number;
  timestamp?: number;
}

const BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export function resolveApiUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BASE}${url}`;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export const api = {
  health: () => request<{ ok: true }>("/api/health"),

  listRuns: (limit = 50) =>
    request<{ runs: RunRow[] }>(`/api/novels?limit=${limit}`),

  getRun: (id: string) => request<{ run: RunRow; bible?: BibleSummary | null }>(`/api/novels/${id}`),

  createRun: (config: Omit<RunConfig, "provider" | "model"> & {
    provider?: string;
    model?: string;
  }) =>
    request<{ runId: string; workflowInstanceId: string }>(`/api/novels`, {
      method: "POST",
      body: JSON.stringify(config),
    }),

  pauseRun: (id: string) =>
    request<{ ok: true }>(`/api/novels/${id}/pause`, { method: "POST" }),

  cancelRun: (id: string) =>
    request<{ ok: true }>(`/api/novels/${id}/cancel`, { method: "POST" }),

  resumeRun: (id: string) =>
    request<{ ok: true }>(`/api/novels/${id}/resume`, { method: "POST" }),

  updateRun: (id: string, updates: { premise?: string }) =>
    request<{ ok: true }>(`/api/novels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),

  decideCheckpoint: (
    id: string,
    chapter: number,
    decision: "approve" | "revise" | "abort",
  ) =>
    request<{ ok: true; decision: string }>(
      `/api/novels/${id}/checkpoint`,
      {
        method: "POST",
        body: JSON.stringify({ chapter, decision }),
      },
    ),

  listChapters: (id: string) =>
    request<{ chapters: ChapterRow[] }>(`/api/novels/${id}/chapters`),

  /** Streamed raw Markdown. Returns a string — caller renders. */
  chapterMarkdown: async (id: string, n: number): Promise<string> => {
    const res = await fetch(`${BASE}/api/novels/${id}/chapters/${n}`);
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.text();
  },

  bibleMarkdown: async (id: string): Promise<string> => {
    const res = await fetch(`${BASE}/api/novels/${id}/bible`);
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.text();
  },

  deleteRun: (id: string) =>
    request<{ ok: true }>(`/api/novels/${id}`, { method: "DELETE" }),

  updateChapter: (id: string, chapterNo: number, content: string) =>
    request<{ ok: true; wordCount: number }>(`/api/novels/${id}/chapters/${chapterNo}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  getBibleFull: (id: string) =>
    request<{ bible: Record<string, any> }>(`/api/novels/${id}/bible/full`),

  updateBibleStructured: (id: string, bibleData: Record<string, any>) =>
    request<{ ok: true }>(`/api/novels/${id}/bible`, {
      method: "PATCH",
      body: JSON.stringify(bibleData),
    }),

  generateCover: (id: string) =>
    request<{ ok: true; coverUrl: string }>(`/api/novels/${id}/cover/generate`, { method: "POST" }),

  generateChapterThumbnail: (id: string, chapterNo: number) =>
    request<{ ok: true; thumbnailUrl: string }>(`/api/novels/${id}/chapters/${chapterNo}/thumbnail/generate`, { method: "POST" }),

  updateSettings: (id: string, settings: { temperature?: number; maxRevisePasses?: number; register?: "cholit" | "sadhu"; premise?: string }) =>
    request<{ ok: true; config: Record<string, any> }>(`/api/novels/${id}`, {
      method: "PATCH",
      body: JSON.stringify(settings),
    }),

  insertChapter: (id: string, chapterNo: number, title?: string, content?: string) =>
    request<{ ok: true; chapterNo: number; id: string }>(`/api/novels/${id}/chapters/insert`, {
      method: "POST",
      body: JSON.stringify({ chapterNo, title, content }),
    }),

  reorderChapters: (id: string, orders: Array<{ id: string; chapterNo: number }>) =>
    request<{ ok: true }>(`/api/novels/${id}/chapters/reorder`, {
      method: "POST",
      body: JSON.stringify({ orders }),
    }),

  search: (id: string, q: string) =>
    request<{ results: Array<{ chapter_no: number; rank: number }> }>(
      `/api/novels/${id}/search?q=${encodeURIComponent(q)}`,
    ),
};

/**
 * Open an SSE stream for a run. The Worker emits either
 * `event: progress` (a JSON-encoded ProgressEvent) or `event: ping`
 * (a keepalive every 15s). Returns an EventSource — caller closes it.
 */
export function openRunEvents(runId: string): EventSource {
  return new EventSource(`${BASE}/api/novels/${runId}/events`);
}
